import React, { useState, useEffect } from 'react';
import { getAllProgrammes } from '../services/api'; 

const Programme = () => {
  const [grille, setGrille] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Liste pour faire correspondre le jour de la semaine en français
  const joursSemaine = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const aujourdhui = joursSemaine[new Date().getDay()];

  useEffect(() => {
    const fetchProgrammes = async () => {
      try {
        const response = await getAllProgrammes();
        const data = Array.isArray(response.data) ? response.data : [];

        // --- LOGIQUE DE TRI : On met les programmes d'aujourd'hui en premier ---
        const sortedData = [...data].sort((a, b) => {
          if (a.jour === aujourdhui && b.jour !== aujourdhui) return -1;
          if (a.jour !== aujourdhui && b.jour === aujourdhui) return 1;
          return 0;
        });

        setGrille(sortedData);
      } catch (error) {
        console.error("Erreur lors de la récupération des programmes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgrammes();
  }, [aujourdhui]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  // LOGIQUE DE SÉLECTION : Le 1er (aujourd'hui) ou la liste complète
  const displayGrille = showAll ? grille : grille.slice(0, 1);

  return (
    <section className="py-4 py-md-5 bg-white shadow-lg rounded-4 rounded-md-5 overflow-hidden my-4 my-md-5 border-top border-5 border-warning" id="grille">
      
      {/* BANDEAU DÉFILANT "EN DIRECT" */}
      <div className="bg-danger py-2 mb-4 overflow-hidden shadow-sm">
        <div className="d-flex whitespace-nowrap marquee-text text-uppercase fw-black text-white opacity-90">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="mx-4 small-mobile-text">En direct 〰️ En direct 〰️ En direct 〰️</span>
          ))}
        </div>
      </div>

      <div className="container px-3 px-md-4">
        <div className="text-center mb-4 mb-md-5">
          <h2 className="fw-black text-uppercase italic display-6 responsive-title">
            Programme <span className="text-danger">Radio</span>
          </h2>
          <div className="bg-warning mx-auto mt-2" style={{height: '4px', width: '60px'}}></div>
          
          {/* TEXTE INDICATIF DYNAMIQUE */}
          {!showAll && grille.length > 0 && (
            <p className="text-muted small mt-3 fw-bold text-uppercase italic animate-fade-in">
              {grille[0].jour === aujourdhui ? "À l'antenne aujourd'hui" : "Prochainement à l'antenne"}
            </p>
          )}
        </div>

        {/* LISTE VERTICALE */}
        <div className="row g-3 g-md-4 px-0 px-md-5">
          {displayGrille.length > 0 ? (
            displayGrille.map((item, index) => {
              const estAujourdhui = item.jour === aujourdhui;
              
              return (
                <div key={item.id || index} className="col-12 animate-fade-in">
                  <div className={`p-3 p-md-4 border-start border-5 rounded-4 transition-all hover-card-program shadow-sm ${estAujourdhui ? 'border-danger bg-light' : 'border-secondary bg-white opacity-75'}`}>
                    <div className="row align-items-center text-center text-lg-start">
                      
                      {/* JOUR ET HEURE */}
                      <div className="col-lg-3 mb-2 mb-lg-0">
                        <span className={`badge ${estAujourdhui ? 'bg-danger' : 'bg-dark'} px-3 py-2 rounded-pill fw-black text-uppercase mb-2 d-inline-block`} style={{fontSize: '10px', letterSpacing: '1px'}}>
                          {item.jour}
                        </span>
                        <div className="text-danger fw-black h5 mb-0 italic responsive-time">{item.heure}</div>
                      </div>

                      {/* TITRE ET ANIMATEUR */}
                      <div className="col-lg-9 mb-3 mb-lg-0 border-start-lg ps-lg-4">
                        <h5 className="fw-black text-uppercase mb-1 text-primary responsive-show-title" style={{letterSpacing: '0.5px'}}>
                          {item.titre}
                        </h5>
                        <p className="fw-bold mb-0 text-secondary small">
                          animé par <span className="text-uppercase text-dark">{item.animateur}</span>
                        </p>
                        {item.description && (
                          <div className="text-muted small italic mt-2 border-top pt-2 description-text" style={{lineHeight: '1.4'}}>
                            {item.description}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted italic py-4">Aucun programme prévu pour le moment.</div>
          )}
        </div>

        {/* BOUTON DÉPLOYABLE */}
        {grille.length > 1 && (
          <div className="text-center mt-5">
            <button 
              onClick={() => setShowAll(!showAll)}
              className="btn btn-warning fw-black text-uppercase px-4 py-2 rounded-pill shadow-sm transition-all"
              style={{ fontSize: '11px', letterSpacing: '1px' }}
            >
              {showAll ? (
                <><i className="bi bi-chevron-compact-up me-2"></i>Réduire le programme</>
              ) : (
                <><i className="bi bi-calendar3 me-2"></i>Voir toute la semaine</>
              )}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .marquee-text {
          display: flex;
          animation: marquee 25s linear infinite;
          white-space: nowrap;
        }

        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .border-start-lg {
          border-left: 2px solid #dee2e6;
        }

        @media (max-width: 991px) {
          .border-start-lg { border-left: none; }
          .responsive-title { font-size: 1.8rem; }
          .responsive-time { font-size: 1.1rem; }
          .responsive-show-title { font-size: 1.1rem; }
          .description-text { font-size: 0.85rem; }
        }

        .hover-card-program {
          border: 1px solid #eee;
          transition: all 0.3s ease;
        }

        .hover-card-program:hover {
          transform: scale(1.01);
          border-color: #ffc107 !important;
          background-color: #fff !important;
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }

        .fw-black { font-weight: 900; }
        .italic { font-style: italic; }
      `}</style>
    </section>
  );
};

export default Programme;